import { Injectable, inject, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { Subject } from 'rxjs';
import { AlertController } from '@ionic/angular';
import {
  PushNotifications,
  PushNotificationSchema,
  ActionPerformed,
  Token,
} from '@capacitor/push-notifications';
import { Device } from '@capacitor/device';
import { v4 as uuidv4 } from 'uuid';
import { DexieDbService } from './dexie/dexie-db-service';
import { AuthService } from './auth-service';
import {
  DevicePushRegistrationPayload,
  StoreesBackendService,
} from './storees-backend.service';
import { Constants } from '../utils/Constants';
import { logger } from '../utils/logger';
import {
  EXISTING_USER_PUSH_PROMPT_DISMISSED,
  PENDING_DAY_STORY,
  PENDING_TRIAL_REMINDER,
  STORY_HOME,
  TABS,
  VOICE_CLONE_SHOW_REVERSE_TRIAL_PAYWALL,
  WELCOME_PUSH_ONBOARDING_COMPLETED,
} from '@storees/shared-constants';
import { SessionService } from './session.service';
import { FirebaseAnalyticsService } from './firebase/FirebaseAnalyticsService';
import {
  FirebaseMessaging,
  Importance,
  Visibility,
  type Notification as FirebaseRemoteNotification,
} from '@capacitor-firebase/messaging';

type PushPlatform = 'ios' | 'android';

interface PushRegistrationState {
  installationId: string;
  pushToken?: string;
  platform?: PushPlatform;
  lastSyncedToken?: string | null;
  lastSyncedAuthSubject?: string | null;
  /** Internal `User.id` from `GET /v1/users/me` at last successful device-push sync. */
  lastSyncedCanonicalUserId?: string | null;
  lastSyncedAt?: string | null;
}

export interface PendingDayStory {
  campaignDate: string;
  receivedAt: string;
  tagline?: string;
  emoji?: string;
  eventName?: string;
}

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  private dexieDb = inject(DexieDbService);
  private storeesBackendService = inject(StoreesBackendService);
  private authService = inject(AuthService);
  private sessionService = inject(SessionService);
  private router = inject(Router);
  private alertController = inject(AlertController);
  private firebaseAnalytics = inject(FirebaseAnalyticsService);
  private ngZone = inject(NgZone);

  private readonly PUSH_REGISTRATION_KEY = Constants.SESSION.PUSH_REGISTRATION;
  private pendingDayStory: PendingDayStory | null = null;
  private pendingDayStoryCapturedSubject = new Subject<PendingDayStory>();
  private listenersInitialized = false;
  private firebaseMessagingListenersInitialized = false;
  private lastForegroundDedupeKey: string | null = null;
  private lastForegroundDedupeAt = 0;
  private registrationWaiters: Array<(token: string) => void> = [];
  private registrationErrorWaiters: Array<(error: Error) => void> = [];
  readonly pendingDayStoryCaptured$ =
    this.pendingDayStoryCapturedSubject.asObservable();

  async getPendingDayStory(): Promise<PendingDayStory | null> {
    if (this.pendingDayStory?.campaignDate) {
      return this.pendingDayStory;
    }

    const pending = (await this.sessionService.getPersistent(
      PENDING_DAY_STORY
    )) as PendingDayStory | null;
    if (pending?.campaignDate) {
      this.pendingDayStory = pending;
      return pending;
    }

    return null;
  }

  async clearPendingDayStory(): Promise<void> {
    this.pendingDayStory = null;
    await this.sessionService.setPersistent(PENDING_DAY_STORY, null);
  }

  async requestPermissionAndRegister(jwtToken?: string): Promise<void> {
    if (!(await this.isNativePushPlatform())) return;

    await this.ensureListeners();
    const permissions = await PushNotifications.checkPermissions();

    let receivePermission = permissions.receive;
    if (receivePermission === 'prompt' || receivePermission === 'prompt-with-rationale') {
      const requested = await PushNotifications.requestPermissions();
      receivePermission = requested.receive;
    }

    await this.firebaseAnalytics.logPushNotificationPermissionResult(
      receivePermission === 'granted'
    );

    if (receivePermission !== 'granted') {
      logger.info('PushNotificationService: Push permission not granted');
      return;
    }

    await this.ensureFirebaseMessagingListeners();
    await this.refreshNativePushTokenFromFirebaseOrFallback();

    try {
      await PushNotifications.register();
    } catch (error) {
      logger.warn('PushNotificationService: PushNotifications.register failed', error);
    }

    if (jwtToken) {
      await this.syncPushTokenFromLocalState(jwtToken);
    }
  }

  /**
   * Requests notification permission and registers the push token using the current session JWT
   * (same behavior as welcome-screen-4).
   */
  async requestPermissionAndRegisterWithSessionJwt(): Promise<void> {
    const jwtToken = await this.authService.getSessionJwtToken();
    await this.requestPermissionAndRegister(jwtToken ?? undefined);
  }

  /**
   * Whether Story Home should show the existing-user push prompt (native app, push not granted,
   * user did not already finish welcome-screen-4, and the modal was not dismissed previously).
   */
  async shouldShowExistingUserPushPrompt(): Promise<boolean> {
    if (!(await this.isNativePushPlatform())) {
      return false;
    }
    if (
      (await this.sessionService.getPersistent(EXISTING_USER_PUSH_PROMPT_DISMISSED)) === 'true'
    ) {
      return false;
    }
    if (
      (await this.sessionService.getPersistent(WELCOME_PUSH_ONBOARDING_COMPLETED)) === 'true'
    ) {
      return false;
    }
    const permissions = await PushNotifications.checkPermissions();
    if (permissions.receive === 'granted') {
      return false;
    }
    return true;
  }

  async markExistingUserPushPromptFinished(): Promise<void> {
    await this.sessionService.setPersistent(EXISTING_USER_PUSH_PROMPT_DISMISSED, 'true');
  }

  async checkRefreshAndSyncPushToken(jwtToken: string): Promise<void> {
    if (!(await this.isNativePushPlatform())) return;

    await this.ensureListeners();
    await this.ensureFirebaseMessagingListeners();

    const permissions = await PushNotifications.checkPermissions();
    if (permissions.receive !== 'granted') {
      logger.info('PushNotificationService: push permission not granted; skip token refresh and sync', {
        receive: permissions.receive,
      });
      return;
    }

    await this.refreshNativePushTokenFromFirebaseOrFallback();

    try {
      await PushNotifications.register();
    } catch (error) {
      logger.warn('PushNotificationService: PushNotifications.register failed', error);
    }

    await this.syncPushTokenFromLocalState(jwtToken);
  }

  async syncPushTokenFromLocalState(jwtToken: string): Promise<void> {
    if (!(await this.isNativePushPlatform())) return;
    if (!jwtToken) return;

    const localState = await this.getPushRegistrationState();
    if (!localState?.pushToken || !localState.platform) {
      logger.info('PushNotificationService: no local FCM token to sync yet', {
        hasInstallationId: Boolean(localState?.installationId),
      });
      return;
    }

    const currentToken = localState.pushToken;
    const currentAuthSubject = this.getPushSyncAuthSubject(jwtToken);

    let canonicalUserId: string;
    try {
      const me = await this.storeesBackendService.getCurrentUser(jwtToken);
      canonicalUserId = me.id;
    } catch (error) {
      logger.error(
        'PushNotificationService: getCurrentUser failed; cannot sync device push without stable userId',
        error
      );
      return;
    }

    if (
      localState.lastSyncedToken === currentToken &&
      currentAuthSubject &&
      localState.lastSyncedAuthSubject === currentAuthSubject &&
      localState.lastSyncedCanonicalUserId === canonicalUserId
    ) {
      logger.info('PushNotificationService: device push already synced; skipping PUT', {
        platform: localState.platform,
        tokenPrefix: currentToken.slice(0, 12),
      });
      return;
    }

    const payload: DevicePushRegistrationPayload = {
      userId: canonicalUserId,
      installationId: localState.installationId,
      token: currentToken,
      platform: localState.platform,
    };

    logger.info(
      'PushNotificationService: PUT device-push — payload.userId is GET /v1/users/me id (stable user id), not UserIdentity.providerUserId',
      {
        platform: localState.platform,
        authSubject: currentAuthSubject,
        userIdPrefix: canonicalUserId.slice(0, 8),
        tokenPrefix: currentToken.slice(0, 12),
      }
    );

    await this.storeesBackendService.upsertDevicePushToken(jwtToken, payload);

    await this.setPushRegistrationState({
      ...localState,
      lastSyncedToken: currentToken,
      lastSyncedAuthSubject: currentAuthSubject,
      lastSyncedCanonicalUserId: canonicalUserId,
      lastSyncedAt: new Date().toISOString(),
    });

    logger.info('PushNotificationService: device push token synced to backend', {
      platform: localState.platform,
      tokenPrefix: currentToken.slice(0, 12),
    });
  }

  /**
   * iOS: FCM cannot mint a registration token until APNs has registered. Call
   * `PushNotifications.register()` and wait for the `registration` event first.
   */
  private async ensureIosApnsRegisteredBeforeFcm(): Promise<void> {
    if ((await this.getNativePlatform()) !== 'ios') {
      return;
    }
    try {
      const waiter = this.waitForRegistrationEvent();
      await PushNotifications.register();
      await waiter;
      logger.info('PushNotificationService: iOS APNs registration completed before FCM getToken');
    } catch (error) {
      logger.warn(
        'PushNotificationService: iOS APNs registration failed before FCM getToken',
        error
      );
    }
  }

  /**
   * Current FCM registration token from Firebase (no unregister). Android fallback uses
   * Capacitor Push `register()` + registration event per plan when Firebase is unavailable.
   */
  private async refreshNativePushTokenFromFirebaseOrFallback(): Promise<void> {
    const platform = await this.getNativePlatform();
    let refreshed = false;

    if (platform === 'ios') {
      await this.ensureIosApnsRegisteredBeforeFcm();
    }

    const maxAttempts = platform === 'ios' ? 3 : 1;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const { token } = await FirebaseMessaging.getToken();
        if (token?.length) {
          const prev = (await this.getPushRegistrationState())?.pushToken;
          await this.updateLocalTokenState(token);
          logger.info('PushNotificationService: FCM token from FirebaseMessaging.getToken', {
            platform,
            attempt,
            tokenChanged: prev !== token,
            tokenPrefix: token.slice(0, 12),
          });
          refreshed = true;
          break;
        }
      } catch (error) {
        logger.warn('PushNotificationService: FirebaseMessaging.getToken failed', {
          attempt,
          platform,
          error,
        });
        if (platform === 'ios' && attempt < maxAttempts) {
          await new Promise((r) => setTimeout(r, 600));
        }
      }
    }

    if (!refreshed && platform === 'android') {
      try {
        const t = await this.registerAndCaptureToken();
        logger.info('PushNotificationService: FCM token from PushNotifications.register (Android)', {
          tokenPrefix: t.slice(0, 12),
        });
        refreshed = true;
      } catch (error) {
        logger.warn('PushNotificationService: PushNotifications register capture failed', error);
      }
    }

    if (!refreshed && platform === 'ios') {
      logger.warn(
        'PushNotificationService: could not obtain FCM token on iOS; if tokenReceived fires later, sync will run then'
      );
    }
  }

  private async ensureFirebaseMessagingListeners(): Promise<void> {
    if (this.firebaseMessagingListenersInitialized) {
      return;
    }

    await FirebaseMessaging.addListener('tokenReceived', async (event) => {
      try {
        await this.updateLocalTokenState(event.token);
        logger.info('PushNotificationService: FCM token rotated (tokenReceived)', {
          tokenPrefix: event.token.slice(0, 12),
        });
        const jwt = await this.authService.getSessionJwtToken();
        if (jwt) {
          await this.syncPushTokenFromLocalState(jwt);
        }
      } catch (error) {
        logger.error('PushNotificationService: Failed to persist rotated FCM token', error);
      }
    });

    await FirebaseMessaging.addListener('notificationReceived', async (event) => {
      try {
        const schema = this.firebaseRemoteNotificationToSchema(event.notification);
        logger.info('PushNotificationService: Firebase notificationReceived', {
          id: schema.id,
          type: schema.data?.['type'],
          title: schema.title,
        });
        if (this.shouldSkipDuplicateForegroundNotification(schema)) {
          return;
        }
        await this.presentForegroundPushNotification(schema);
      } catch (error) {
        logger.error('PushNotificationService: Firebase notificationReceived handler failed', error);
      }
    });

    await FirebaseMessaging.addListener('notificationActionPerformed', async (event) => {
      try {
        const schema = this.firebaseRemoteNotificationToSchema(event.notification);
        const action: ActionPerformed = {
          actionId: event.actionId,
          notification: schema,
          inputValue: event.inputValue,
        };
        await this.captureDailyStoryPushAction(action);
      } catch (error) {
        logger.error(
          'PushNotificationService: Firebase notificationActionPerformed handler failed',
          error
        );
      }
    });

    this.firebaseMessagingListenersInitialized = true;
  }

  private async registerAndCaptureToken(): Promise<string> {
    const waiter = this.waitForRegistrationEvent();
    await PushNotifications.register();
    return waiter;
  }

  private waitForRegistrationEvent(): Promise<string> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.registrationWaiters = this.registrationWaiters.filter(
          (waiter) => waiter !== onToken
        );
        this.registrationErrorWaiters = this.registrationErrorWaiters.filter(
          (waiter) => waiter !== onError
        );
        reject(new Error('Push token registration timed out'));
      }, 10000);

      const onToken = (token: string) => {
        clearTimeout(timeout);
        resolve(token);
      };

      const onError = (error: Error) => {
        clearTimeout(timeout);
        reject(error);
      };

      this.registrationWaiters.push(onToken);
      this.registrationErrorWaiters.push(onError);
    });
  }

  private async ensureListeners(): Promise<void> {
    if (this.listenersInitialized) return;

    await PushNotifications.addListener('registration', async (token: Token) => {
      const value = token.value;
      try {
        const platform = await this.getNativePlatform();
        // iOS registration value is APNs; backend uses FCM only (FirebaseMessaging.getToken).
        if (platform === 'android') {
          await this.updateLocalTokenState(value);
        }
      } catch (error) {
        logger.error('PushNotificationService: Failed to persist token in Dexie', error);
      }

      this.registrationWaiters.splice(0).forEach((resolve) => resolve(value));
      this.registrationErrorWaiters.splice(0);
    });

    await PushNotifications.addListener('registrationError', (error) => {
      const registrationError = new Error(JSON.stringify(error));
      logger.error('PushNotificationService: Registration error', error);

      this.registrationErrorWaiters
        .splice(0)
        .forEach((reject) => reject(registrationError));
      this.registrationWaiters.splice(0);
    });

    await PushNotifications.addListener(
      'pushNotificationReceived',
      async (notification: PushNotificationSchema) => {
        logger.info('PushNotificationService: Capacitor pushNotificationReceived', {
          id: notification.id,
          type: notification.data?.['type'],
          title: notification.title,
        });
        if (this.shouldSkipDuplicateForegroundNotification(notification)) {
          return;
        }
        await this.presentForegroundPushNotification(notification);
      }
    );

    await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      async (action: ActionPerformed) => {
        logger.debug('PushNotificationService: Push action performed', action);
        await this.captureDailyStoryPushAction(action);
      }
    );

    await this.ensureAndroidChannel();
    this.listenersInitialized = true;
  }

  private firebaseRemoteNotificationToSchema(
    notification: FirebaseRemoteNotification
  ): PushNotificationSchema {
    return {
      id: notification.id,
      title: notification.title,
      body: notification.body,
      data: this.mergePushUserInfoData(notification.data),
    } as PushNotificationSchema;
  }

  /** Merges flat + nested FCM/APNs userInfo strings so `type` and campaign keys are visible. */
  private mergePushUserInfoData(raw: unknown): Record<string, string | undefined> {
    const normalized = this.normalizeFirebaseDataPayload(raw) ?? {};
    const out: Record<string, string | undefined> = { ...normalized };
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
        if (k === 'aps' || out[k] !== undefined) {
          continue;
        }
        if (typeof v === 'string') {
          out[k] = v;
        } else if (typeof v === 'number' || typeof v === 'boolean') {
          out[k] = String(v);
        }
      }
    }
    return out;
  }

  /**
   * FCM `data` values are stringified on the wire; iOS `userInfo` may nest custom keys one level
   * under non-`aps` objects—merge string values so `type` is detectable in foreground.
   */
  private normalizeFirebaseDataPayload(
    data: unknown
  ): Record<string, string | undefined> | undefined {
    if (data == null) {
      return undefined;
    }
    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data) as unknown;
        if (parsed && typeof parsed === 'object') {
          return this.normalizeFirebaseDataPayload(parsed);
        }
      } catch {
        return { _: data };
      }
      return undefined;
    }
    if (typeof data !== 'object') {
      return undefined;
    }
    const obj = data as Record<string, unknown>;
    const out: Record<string, string | undefined> = {};

    const putStr = (k: string, v: unknown) => {
      if (typeof v === 'string') {
        out[k] = v;
      } else if (v != null && (typeof v === 'number' || typeof v === 'boolean')) {
        out[k] = String(v);
      }
    };

    for (const [key, value] of Object.entries(obj)) {
      if (key === 'aps') {
        continue;
      }
      if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
      ) {
        putStr(key, value);
        continue;
      }
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        for (const [innerKey, innerVal] of Object.entries(
          value as Record<string, unknown>
        )) {
          if (out[innerKey] === undefined) {
            putStr(innerKey, innerVal);
          }
        }
      }
    }
    return Object.keys(out).length ? out : undefined;
  }

  private isSubscriptionTrialReminder(
    notification: PushNotificationSchema,
    data: Record<string, string | undefined> | undefined
  ): boolean {
    if (data?.['type'] === 'subscription_trial_ending') {
      return true;
    }
    if (data?.['type'] === 'voice_clone_trial_ending') {
      return true;
    }
    const title = (notification.title ?? '').toLowerCase();
    const body = (notification.body ?? '').toLowerCase();
    if (title.includes('free trial') && title.includes('ends')) {
      return true;
    }
    if (
      body.includes('free trial') &&
      (body.includes('end') || body.includes('premium') || body.includes('subscribe'))
    ) {
      return true;
    }
    return false;
  }

  /**
   * Trial reminder: always interrupt with the same copy as the push (no Story Home queue).
   */
  private async presentTrialReminderInterruptAlert(
    notification: PushNotificationSchema
  ): Promise<void> {
    await this.sessionService.setPersistent(PENDING_TRIAL_REMINDER, null);
    const title =
      typeof notification.title === 'string' && notification.title.trim().length > 0
        ? notification.title.trim()
        : 'Subscription';
    const body =
      typeof notification.body === 'string' && notification.body.trim().length > 0
        ? notification.body.trim()
        : '';
    await this.ngZone.run(async () => {
      const alert = await this.alertController.create({
        header: title,
        message: body,
        buttons: ['OK'],
      });
      await alert.present();
    });
    logger.info('PushNotificationService: Trial reminder interrupt alert presented');
  }

  /**
   * Android may deliver the same foreground message to both Capacitor Push and Firebase Messaging.
   */
  private shouldSkipDuplicateForegroundNotification(
    notification: PushNotificationSchema
  ): boolean {
    const data = this.mergePushUserInfoData(notification.data);
    const key = `${notification.id ?? ''}|${notification.title ?? ''}|${notification.body ?? ''}|${data?.['type'] ?? ''}|${data?.['campaignDate'] ?? ''}`;
    const now = Date.now();
    if (key === this.lastForegroundDedupeKey && now - this.lastForegroundDedupeAt < 900) {
      logger.debug('PushNotificationService: Skipping duplicate foreground notification', {
        id: notification.id,
      });
      return true;
    }
    this.lastForegroundDedupeKey = key;
    this.lastForegroundDedupeAt = now;
    return false;
  }

  private async captureDailyStoryPushAction(
    action: ActionPerformed
  ): Promise<void> {
    const n = action.notification as PushNotificationSchema;
    const data = this.mergePushUserInfoData(n?.data);
    if (data?.['type'] === 'voice_clone_trial_ending') {
      await this.sessionService.setPersistent(PENDING_TRIAL_REMINDER, null);
      this.sessionService.set(VOICE_CLONE_SHOW_REVERSE_TRIAL_PAYWALL, 'true');
      await this.navigateToStoryHomeIfNeeded();
      return;
    }
    if (data?.['type'] === 'streak_expiring') {
      await this.navigateToStoryHomeIfNeeded();
      logger.info('PushNotificationService: Navigated to StoryHome (streak expiring tap)');
      return;
    }
    if (this.isSubscriptionTrialReminder(n, data)) {
      await this.navigateToStoryHomeIfNeeded();
      return;
    }
    await this.captureDailyStoryPushData(data, 'tap', n?.title);
  }

  /** StoryHome tab path segment, e.g. `/tabs/story-home`. */
  private isStoryHomeRoute(): boolean {
    return this.router.url.includes(`/${STORY_HOME}`);
  }

  private async navigateToStoryHomeIfNeeded(): Promise<void> {
    if (this.isStoryHomeRoute()) {
      logger.info('PushNotificationService: StoryHome already active (subscription trial tap)');
      return;
    }
    const didNavigate = await this.router.navigate([`${TABS}/${STORY_HOME}`]);
    logger.info('PushNotificationService: Navigated to StoryHome (subscription trial tap)', {
      didNavigate,
    });
  }

  /**
   * Foreground: OS banners handle visibility (see `capacitor.config` presentationOptions). Trial
   * reminders no longer use a blocking Ionic alert on receipt. Queue for Story Home only when the
   * user is on another tab (not Story Home / story player) so they still get an in-app hint after
   * navigating home if they dismissed the banner.
   */
  private async presentForegroundPushNotification(
    notification: PushNotificationSchema
  ): Promise<void> {
    const data = this.mergePushUserInfoData(notification.data);

    if (this.isSubscriptionTrialReminder(notification, data)) {
      await this.presentTrialReminderInterruptAlert(notification);
      return;
    }

    if (data?.['type'] === 'streak_expiring') {
      logger.debug('PushNotificationService: Ignoring streak_expiring foreground push (background-only)');
      return;
    }

    if (!data || data['type'] !== 'day_story' || !data['campaignDate']) {
      logger.debug('PushNotificationService: Ignoring non-day-story foreground push', {
        type: data?.['type'],
        campaignDate: data?.['campaignDate'],
      });
      return;
    }

    if (!this.isOnStoryHomeScreen()) {
      logger.debug(
        'PushNotificationService: Ignoring foreground day-story prompt outside StoryHome',
        {
          currentUrl: this.router.url,
          campaignDate: data['campaignDate'],
        }
      );
      return;
    }

    await this.ngZone.run(async () => {
      const alert = await this.alertController.create({
        header: notification.title || 'Story of the Day',
        message:
          notification.body ||
          data['tagline'] ||
          'Today\'s story is ready. Do you want to open it now?',
        buttons: [
          {
            text: 'No thanks',
            role: 'cancel',
          },
          {
            text: 'Open',
            handler: () => {
              void this.captureDailyStoryPushData(data, 'tap', notification.title);
            },
          },
        ],
      });
      await alert.present();
    });
  }

  private isOnStoryHomeScreen(): boolean {
    const [path] = this.router.url.split(/[?#]/, 1);
    return path.replace(/\/+$/, '') === `/${TABS}/${STORY_HOME}`;
  }

  /**
   * Extracts the event name from a push notification title like "🐬 National Dolphin Day".
   * Strips leading emoji/whitespace so the result is e.g. "National Dolphin Day".
   */
  private static extractEventNameFromTitle(title: string | undefined): string | undefined {
    if (!title) return undefined;
    const stripped = title.replace(/^[\p{Emoji_Presentation}\p{Extended_Pictographic}\s]+/u, '').trim();
    return stripped.length > 0 ? stripped : undefined;
  }

  private async captureDailyStoryPushData(
    data: Record<string, string | undefined> | undefined,
    source: 'tap',
    notificationTitle?: string
  ): Promise<void> {
    if (!data || data['type'] !== 'day_story' || !data['campaignDate']) {
      logger.debug('PushNotificationService: Ignoring non-day-story push payload', {
        type: data?.['type'],
        campaignDate: data?.['campaignDate'],
        source,
      });
      return;
    }

    const eventName =
      data['eventName'] ||
      PushNotificationService.extractEventNameFromTitle(notificationTitle);

    const pendingDayStory: PendingDayStory = {
      campaignDate: data['campaignDate'],
      receivedAt: new Date().toISOString(),
      tagline: data['tagline'],
      emoji: data['emoji'],
      eventName,
    };

    logger.info('PushNotificationService: Captured pending day story from push notification', {
      campaignDate: pendingDayStory.campaignDate,
      receivedAt: pendingDayStory.receivedAt,
      tagline: pendingDayStory.tagline ?? null,
      emoji: pendingDayStory.emoji ?? null,
      eventName: pendingDayStory.eventName ?? null,
      source,
    });

    this.pendingDayStory = pendingDayStory;
    await this.sessionService.setPersistent(PENDING_DAY_STORY, pendingDayStory);
    this.pendingDayStoryCapturedSubject.next(pendingDayStory);
    logger.info('PushNotificationService: Stored pending day story for StoryHome handoff', {
      campaignDate: pendingDayStory.campaignDate,
      source,
    });

    const jwtToken = await this.authService.getSessionJwtToken();
    if (jwtToken && source === 'tap') {
      try {
        await this.storeesBackendService.postPushInteraction(jwtToken, {
          campaignType: 'day_story',
          campaignDate: pendingDayStory.campaignDate,
          action: 'tap',
        });
        logger.info('PushNotificationService: Recorded push interaction for pending day story', {
          campaignDate: pendingDayStory.campaignDate,
        });
      } catch (error) {
        logger.warn('PushNotificationService: Failed to post push interaction', error);
      }
    }

    if (this.router.url.includes(`/${STORY_HOME}`)) {
      logger.info('PushNotificationService: StoryHome already active; awaiting in-app handoff', {
        campaignDate: pendingDayStory.campaignDate,
        source,
      });
      return;
    }

    const didNavigate = await this.router.navigate([`${TABS}/${STORY_HOME}`]);
    logger.info('PushNotificationService: Navigated to StoryHome after push notification', {
      campaignDate: pendingDayStory.campaignDate,
      didNavigate,
      source,
    });
  }

  private async ensureAndroidChannel(): Promise<void> {
    const platform = await this.getNativePlatform();
    if (platform !== 'android') return;

    try {
      await PushNotifications.createChannel({
        id: 'storees_default',
        name: 'Storees',
        importance: 4,
        description: 'Default channel for Storees notifications',
      });
      await FirebaseMessaging.createChannel({
        id: 'storees_default',
        name: 'Storees',
        description: 'Default channel for Storees notifications',
        importance: Importance.High,
        visibility: Visibility.Public,
      });
    } catch (error) {
      logger.warn('PushNotificationService: Failed to create Android channel', error);
    }
  }

  private async updateLocalTokenState(pushToken: string): Promise<void> {
    const existingState = await this.getPushRegistrationState();
    const platform = await this.getNativePlatform();
    if (!platform) return;

    const installationId =
      existingState?.installationId || (await this.generateInstallationId());
    const tokenChanged = existingState?.pushToken !== pushToken;
    const platformChanged = existingState?.platform !== platform;

    const nextState: PushRegistrationState = {
      installationId,
      pushToken,
      platform,
      lastSyncedToken:
        tokenChanged || platformChanged
          ? null
          : (existingState?.lastSyncedToken ?? null),
      lastSyncedAuthSubject:
        tokenChanged || platformChanged
          ? null
          : (existingState?.lastSyncedAuthSubject ?? null),
      lastSyncedCanonicalUserId:
        tokenChanged || platformChanged
          ? null
          : (existingState?.lastSyncedCanonicalUserId ?? null),
      lastSyncedAt:
        tokenChanged || platformChanged
          ? null
          : (existingState?.lastSyncedAt ?? null),
    };

    await this.setPushRegistrationState(nextState);
  }

  private async getPushRegistrationState(): Promise<PushRegistrationState | null> {
    const rawValue = await this.dexieDb.getAppSessionValue(this.PUSH_REGISTRATION_KEY);
    if (!rawValue) return null;

    try {
      const parsed = JSON.parse(rawValue) as PushRegistrationState;
      if (!parsed?.installationId) return null;
      return parsed;
    } catch (error) {
      logger.warn('PushNotificationService: Failed to parse local push state', error);
      return null;
    }
  }

  private async setPushRegistrationState(state: PushRegistrationState): Promise<void> {
    await this.dexieDb.setAppSessionValue(
      this.PUSH_REGISTRATION_KEY,
      JSON.stringify(state)
    );
  }

  private getPushSyncAuthSubject(jwtToken: string): string | null {
    try {
      const payloadBase64 = jwtToken.split('.')[1];
      if (!payloadBase64) {
        return null;
      }

      const payload = JSON.parse(atob(payloadBase64)) as Record<string, unknown>;
      const anonymousId = payload['anonymousId'];
      if (typeof anonymousId === 'string' && anonymousId.length > 0) {
        return `anonymous:${anonymousId}`;
      }

      const subject = payload['sub'];
      if (typeof subject === 'string' && subject.length > 0) {
        return `sub:${subject}`;
      }

      return null;
    } catch (error) {
      logger.warn(
        'PushNotificationService: Failed to resolve push sync auth subject',
        error
      );
      return null;
    }
  }

  private async generateInstallationId(): Promise<string> {
    try {
      const deviceId = await Device.getId();
      if (deviceId?.identifier) {
        return deviceId.identifier;
      }
    } catch (error) {
      logger.warn('PushNotificationService: Failed to resolve device identifier', error);
    }
    return uuidv4();
  }

  private async isNativePushPlatform(): Promise<boolean> {
    return (await this.getNativePlatform()) !== null && Capacitor.isNativePlatform();
  }

  private async getNativePlatform(): Promise<PushPlatform | null> {
    const info = await Device.getInfo();
    if (info.platform === 'ios' || info.platform === 'android') {
      return info.platform;
    }
    return null;
  }
}
